import { PatientSearchSelect } from '@/components/session/PatientSearchSelect';
import { Button } from '@/components/ui/button';
import { normalizeKennitala } from '@/hooks/usePatientLookup';
import { usePatientSearch } from '@/hooks/usePatientSearch';
import type { ReferralProvider, ReferralResult, ReferralState } from '@/types/referrals';
import type { Patient } from '@/types/session';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { type Fields, type Props, defaults, missingPatient, schema } from './ReferralDeliveryPanel.types';
import { ReferralRecipientFields } from './ReferralRecipientFields';
import { ReferralSendFeedback } from './ReferralSendFeedback';
import { ReferralRequestError, referralRequest } from './api';

export const ReferralDeliveryPanel = ({ taskId, sessionId, letter, editor, state, onSaved, onReload }: Props) => {
  const id = useId();
  const client = useQueryClient();
  const data = state.data;
  const form = useForm<Fields>({
    resolver: zodResolver(schema),
    defaultValues: defaults(data?.draft ?? null),
    mode: 'onChange',
  });
  const [message, setMessage] = useState<string | null>(null);
  const [needsReload, setNeedsReload] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const intent = useRef<string | null>(null);
  const dispatching = useRef(false);
  const initialRevision = useRef<number | null>(null);
  const patient = usePatientSearch({ persistSelection: false, lookupPatientInfo: false });
  const fields = form.watch();
  const active = Boolean(data?.enabled && !data.archived && state.canWrite);

  useEffect(() => {
    if (data && initialRevision.current === null) {
      form.reset(defaults(data.draft));
      initialRevision.current = data.revision;
    }
  }, [data, form]);

  useEffect(() => {
    if (data?.archived && data.draft && letter !== data.draft.reason_for_referral) {
      onReload(data.draft.reason_for_referral);
    }
  }, [data?.archived, data?.draft, letter, onReload]);

  const providers = useQuery({
    queryKey: [...state.key, 'providers'],
    enabled: active,
    retry: false,
    queryFn: () => referralRequest<{ data: ReferralProvider[] }>('/api/referrals/providers'),
  });
  const rows = providers.data?.data.filter((row) => row.enabled && row.receives_referrals) ?? [];
  const selected = rows.find(
    (row) => row.id === fields.service_provider_id && row.service_name === fields.service_name
  );
  const revision = data?.revision ?? 0;
  const locked = state.locked || !active || needsReload;
  const invalidate = async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: state.key, exact: true }),
      client.invalidateQueries({ queryKey: [...state.key, 'attempt'] }),
      client.invalidateQueries({ queryKey: ['todos'] }),
      sessionId ? client.invalidateQueries({ queryKey: ['tasks', sessionId], exact: true }) : Promise.resolve(),
    ]);
  };
  const failed = (error: unknown) => {
    setMessage('Ekki tókst að ljúka aðgerðinni. Athugaðu stöðu tilvísunar áður en haldið er áfram.');
    if (error instanceof ReferralRequestError && error.code === 'stale_draft') setNeedsReload(true);
  };

  const save = useMutation({
    retry: false,
    mutationFn: () =>
      referralRequest<ReferralState>(`/api/tasks/${taskId}/referral/draft`, 'PUT', {
        expected_revision: revision,
        draft: {
          ...fields,
          reason_for_referral: letter,
          priority: Number(fields.priority),
          valid_until: new Date(`${fields.valid_until}T23:59:59Z`).toISOString(),
        },
      }),
    onSuccess: async (result) => {
      if (!result.draft) return;
      intent.current = null;
      setUncertain(false);
      setMessage('Drög vistuð.');
      form.reset(defaults(result.draft));
      onReload(result.draft.reason_for_referral);
      onSaved(result.draft);
      await invalidate();
    },
    onError: failed,
  });
  const bind = useMutation({
    retry: false,
    mutationFn: (selectedPatient: Patient) => {
      const ssn = normalizeKennitala(selectedPatient?.id);
      if (!selectedPatient || !ssn) throw new Error('Veldu sjúkling.');
      return referralRequest<ReferralState>(`/api/tasks/${taskId}/referral/patient`, 'POST', {
        expected_revision: revision,
        ssn,
        name: selectedPatient.registryName || selectedPatient.name,
      });
    },
    onSuccess: async () => {
      setMessage('Sjúklingur vistaður.');
      await invalidate();
    },
    onError: failed,
  });
  const send = useMutation({
    retry: false,
    mutationFn: (intentId: string) =>
      referralRequest<ReferralResult>(`/api/tasks/${taskId}/referral/send`, 'POST', {
        intent_id: intentId,
        expected_revision: revision,
      }),
    onSuccess: async (result) => {
      setUncertain(Boolean(result.outcome_persistence_pending));
      client.setQueryData<ReferralState>(state.key, (current) =>
        current
          ? {
              ...current,
              attempt: result.attempt ?? current.attempt,
              has_attempt: true,
              editable: false,
              hold_reason: result.hold_reason ?? current.hold_reason,
            }
          : current
      );
      await invalidate();
    },
    onError: (error) => {
      if (!(error instanceof ReferralRequestError) || ['unavailable', 'persistence_unavailable'].includes(error.code))
        setUncertain(true);
      failed(error);
      void invalidate();
    },
    onSettled: () => {
      dispatching.current = false;
    },
  });
  const receipt = useMutation({
    retry: false,
    mutationFn: () =>
      referralRequest<ReferralResult>(`/api/tasks/${taskId}/referral/attempts/${data?.attempt?.id}/receipt`, 'POST'),
    onSuccess: async (result) => {
      setUncertain(Boolean(result.outcome_persistence_pending));
      await invalidate();
    },
    onError: failed,
  });
  const busy = save.isPending || bind.isPending || send.isPending || receipt.isPending;
  const valuesMatch =
    data?.draft &&
    Object.entries(defaults(data.draft)).every(([key, value]) => fields[key as keyof Fields] === value) &&
    letter === data.draft.reason_for_referral;
  const reviewedAgain = !data?.attempt || data.revision > data.attempt.draft_revision;
  const canSend =
    !locked &&
    !busy &&
    !uncertain &&
    !form.formState.isDirty &&
    Boolean(valuesMatch && selected && data?.patient_valid && data?.sender_ready && reviewedAgain);
  const checkUncertainIntent = useMutation({
    retry: false,
    mutationFn: async () => {
      if (data?.attempt) {
        await receipt.mutateAsync();
        return;
      }
      if (!intent.current) return;
      const result = await referralRequest<ReferralResult>(`/api/tasks/${taskId}/referral/intents/${intent.current}`);
      if (result.attempt) await invalidate();
      else setMessage('Engin staðfest niðurstaða enn. Ekki senda tilvísunina aftur.');
    },
    onError: failed,
  });
  const reload = async () => {
    const result = await state.refetch();
    if (result.data) {
      form.reset(defaults(result.data.draft));
      onReload(result.data.draft?.reason_for_referral ?? letter);
      setNeedsReload(false);
      setMessage(null);
    }
  };
  if (!data) return null;

  return (
    <section className="space-y-3" aria-label="Sending tilvísunar">
      {data.archived && (
        <p className="text-sm">
          Upptöku hefur verið eytt eða hún aftengd. Sendingarsaga tilvísunar er varðveitt og henni er ekki hægt að
          breyta.
        </p>
      )}
      <ReferralRecipientFields
        id={id}
        disabled={locked || busy}
        form={form}
        fields={fields}
        rows={rows}
        loading={providers.isPending}
      />
      <fieldset disabled={busy || locked || data.has_attempt} className="space-y-1">
        <legend className="text-sm">Sjúklingur</legend>
        <PatientSearchSelect
          {...patient}
          selectedPatient={
            data.patient_ssn
              ? {
                  id: data.patient_ssn,
                  ssn: `${data.patient_ssn.slice(0, 6)}-${data.patient_ssn.slice(6)}`,
                  name: data.patient_name || '',
                  dob: '',
                }
              : null
          }
          setSelectedPatient={(value) => {
            if (value && !busy && !locked && !data.has_attempt) bind.mutate(value);
          }}
          allowClear={false}
          selectionNotice={false}
        />
        {!data.patient_ssn && data.patient_name && <p className="text-sm">{data.patient_name}</p>}
      </fieldset>
      {!data.patient_valid && (
        <p id={`${id}-patient-help`} className="text-sm">
          {missingPatient}
        </p>
      )}
      {!data.sender_ready && data.enabled && (
        <p className="text-sm">Upplýsingar um sendanda vantar. Hafðu samband við umsjónaraðila.</p>
      )}
      {data.attempt?.state === 'rejected' && !reviewedAgain && (
        <p className="text-sm">Farðu yfir og vistaðu drögin áður en ný sending er reynd.</p>
      )}
      {editor}
      {providers.isError && data.enabled && (
        <p role="alert" className="text-sm">
          Ekki tókst að sækja viðtakendur.
        </p>
      )}
      {message && <output className="text-sm">{message}</output>}
      {needsReload && (
        <Button type="button" variant="outline" onClick={() => void reload()}>
          Sækja nýjustu drög
        </Button>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          disabled={locked || busy || !letter.trim()}
          onClick={form.handleSubmit(() => save.mutate())}
        >
          Vista drög
        </Button>
        <div className="flex min-w-0 items-center gap-3">
          <span
            tabIndex={!data.patient_valid ? 0 : undefined}
            aria-describedby={!data.patient_valid ? `${id}-patient-help` : undefined}
            title={!data.patient_valid ? missingPatient : undefined}
          >
            <Button
              type="button"
              disabled={!canSend}
              aria-describedby={!data.patient_valid ? `${id}-patient-help` : undefined}
              onClick={() => {
                if (dispatching.current) return;
                dispatching.current = true;
                intent.current ??= crypto.randomUUID();
                send.mutate(intent.current);
              }}
            >
              Senda tilvísun
            </Button>
          </span>
          <ReferralSendFeedback
            state={data.attempt?.state}
            pending={send.isPending}
            uncertain={uncertain || Boolean(data.hold_reason)}
            failed={send.isError}
          />
        </div>
        {(uncertain ||
          ['sending', 'unknown', 'needs_investigation'].includes(data.attempt?.state ?? '') ||
          data.hold_reason) && (
          <Button
            type="button"
            variant="outline"
            disabled={busy || checkUncertainIntent.isPending || !state.canWrite}
            onClick={() => checkUncertainIntent.mutate()}
          >
            Athuga sendingu
          </Button>
        )}
      </div>
    </section>
  );
};
